import { OutDealDTO, OutProductExtendedViewDTO } from "@app/bf-api";
import { findPlatformContent, getAbsolutePageUrl } from "@app/api/wp-page-fetcher/utils/platform-content-array";
import { getWebSiteId } from "@app/api/wp-page-fetcher/utils/schema-urls";
import { getGtinProperty } from "@app/api/wp-page-fetcher/utils/get-gtin-property";
import {
  getSchemaAvailability,
  SCHEMA_ITEM_CONDITION,
  toSchemaPrice
} from "@app/api/wp-page-fetcher/utils/schema-offer-values";
import { stripHtmlForSchema } from "@app/api/wp-page-fetcher/utils/strip-html-for-schema";
import { JsonLdNode } from "@app/api/wp-page-fetcher/utils/to-json-ld";

export interface IProductStructuredData {
  /** WebPage and Product, for the page's `@graph`. */
  nodes: JsonLdNode[];
  /** Deals that became an Offer of the Product; a deal tile for one of them must not add a second description. */
  offerDealIds: number[];
}

export interface IProductStructuredDataProps {
  pageUrl: string;
  platformId: number;
  product: OutProductExtendedViewDTO;
  seoDescription: string;
  seoTitle: string;
}

/** A single shop's offer for this product, mirroring one row of the comparison list. */
const getOffer = (deal: OutDealDTO, price: string, url: string, fallbackCurrency: string) => {
  const sellerName = deal.merchant?.name;

  return {
    "@type": "Offer",
    // Anchored on the deal id rather than the shop name: one shop can list the same product twice.
    "@id": `${url}#offer-${deal.id}`,
    url: deal.url || undefined,
    price,
    priceCurrency: deal.currency || fallbackCurrency,
    availability: getSchemaAvailability(deal.stock),
    itemCondition: SCHEMA_ITEM_CONDITION,
    ...(sellerName ? { seller: { "@type": "Organization", name: sellerName } } : {})
  };
};

/**
 * The price range across the offers. Low and high come from the actual prices rather than the first
 * and last deal, so the range stays correct no matter how the API orders them. `offerCount` counts
 * the same set, so it can never describe more offers than the range covers.
 */
const getAggregateOffer = (prices: string[], currency: string) => {
  if (!prices.length) return undefined;

  const amounts = prices.map(Number);

  return {
    "@type": "AggregateOffer",
    lowPrice: Math.min(...amounts).toFixed(2),
    highPrice: Math.max(...amounts).toFixed(2),
    offerCount: prices.length,
    priceCurrency: currency
  };
};

/**
 * Nodes for a product comparison page: a WebPage whose `mainEntity` is the one product being
 * compared, carrying both a price range and every individual shop offer. The page composes them
 * into its single `@graph` together with the deal tiles it shows, see `buildPageStructuredData`.
 * `offerDealIds` says which deals that graph may leave out: only those with an Offer here, so a
 * priceless deal shown as a tile is still described once.
 *
 * `category` stays empty in practice: the API does not serialise `categories` on deals, unlike
 * `brands`. `priceValidUntil` is absent because deals have no end date, and `review` and
 * `aggregateRating` may only be added once the ratings are our own.
 */
export const getProductStructuredData = (props: IProductStructuredDataProps): IProductStructuredData => {
  const { product, platformId, pageUrl, seoTitle, seoDescription } = props;

  const platform = findPlatformContent(platformId);
  const url = getAbsolutePageUrl(platformId, pageUrl);
  const deals = product.deals || [];

  const currency = deals.find((deal) => deal.currency)?.currency || platform.currency;
  const brandName = deals.find((deal) => deal.brands?.[0]?.name)?.brands?.[0]?.name;
  // The product has no category of its own, so it inherits one from its first categorised deal.
  const category = deals.find((deal) => deal.categories?.[0]?.name)?.categories?.[0]?.name;

  // A deal without a usable price is skipped rather than published as a priceless offer: Google
  // expects a price on every Offer, and an offer that names no price says nothing useful.
  const pricedDeals = deals
    .map((deal) => ({ deal, price: toSchemaPrice(deal.price) }))
    .filter((entry): entry is { deal: OutDealDTO; price: string } => !!entry.price);

  const aggregateOffer = getAggregateOffer(
    pricedDeals.map((entry) => entry.price),
    currency
  );

  const offers = [
    ...(aggregateOffer ? [aggregateOffer] : []),
    ...pricedDeals.map((entry) => getOffer(entry.deal, entry.price, url, currency))
  ];

  const productNode: JsonLdNode = {
    "@type": "Product",
    "@id": `${url}#product`,
    name: product.title,
    url,
    // BF-5072 fills this central description with AI copy, so it starts working once that lands.
    // The SEO page text is not used as a stand-in: that is page copy serving a different purpose.
    description: stripHtmlForSchema(product.description) || product.title,
    image: product.image?.url || undefined,
    ...getGtinProperty(product.ean),
    ...(brandName ? { brand: { "@type": "Brand", name: brandName } } : {}),
    ...(category ? { category } : {}),
    // An offline product has no priced deals left; an empty array would claim it has no offers.
    ...(offers.length ? { offers } : {})
  };

  const webPage: JsonLdNode = {
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: seoTitle,
    description: seoDescription,
    inLanguage: platform.locale,
    isPartOf: { "@id": getWebSiteId(platformId) },
    mainEntity: { "@id": productNode["@id"] }
  };

  return { nodes: [webPage, productNode], offerDealIds: pricedDeals.map((entry) => entry.deal.id) };
};
